// Seed data for the catalogs a first visit has no cache for yet (the background sync later uses 'published_at' to
// decide whether newer data must be fetched from the server).
//
// UX roadmap #20: the three JSON catalogs (~213 KB raw, ~58 KB gzip) used to be static imports here, so they sat in
// the entry chunk and every visit downloaded and parsed them, although only an empty cache (or a course route) reads
// them, and any edit to one re-hashed the whole entry. Each now lives in its own module under services/seed/ and is
// fetched on demand. Always go through these loaders; a static import of a seed module puts its JSON back in the entry.
// Two remain, authors and products: courses are read live from the content service since tmi-ck2.44.
import type { Author, Product } from "@tmi/types/entities";
import { clientStateManager } from "@/services/error-services";

/**
 * Imports one seed chunk. A failed download (offline, or a deploy replaced the chunk this tab points at) is reported
 * here, because the store's logger is silent in production and the member is left with an empty catalog, then
 * rethrown. It is not a CacheError, so the store's init latch stays open and the next initialize() tries again.
 */
async function loadSeed< T >( seed: string, load: () => Promise< T[] > ): Promise< T[] > {
    try {
        return await load();
    } catch ( err: unknown ) {
        clientStateManager.queueClientError( err, { seed } );
        throw err;
    }
}

export function loadAuthorSeedData(): Promise< Author[] > {
    return loadSeed( 'authors', async () => ( await import( "@/services/seed/author-seed" ) ).authorSeedData() );
}

export function loadProductSeedData(): Promise< Product[] > {
    return loadSeed( 'products', async () => ( await import( "@/services/seed/product-seed" ) ).productSeedData() );
}
